
import { TrendingUp, Users, Target } from "lucide-react";
import { MarketSizeChart } from "../MarketSizeChart";

interface MarketMetricsProps {
  tam: number;
  sam: number;
  som: number;
  currency: string;
  tamCustomers?: number;
  samCustomers?: number;
  somCustomers?: number;
  sizeMetric: "money" | "customers" | "both";
}

export const MarketMetrics = ({ 
  tam, 
  sam, 
  som, 
  currency,
  tamCustomers = 0,
  samCustomers = 0,
  somCustomers = 0,
  sizeMetric
}: MarketMetricsProps) => {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency,
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(value);
  };

  const formatCustomers = (value: number) => {
    return new Intl.NumberFormat('en-GB', {
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(value);
  };

  const showMoneyMetrics = sizeMetric === "money" || sizeMetric === "both";
  const showCustomerMetrics = sizeMetric === "customers" || sizeMetric === "both";

  return (
    <div className="space-y-6">
      {showMoneyMetrics && (
        <div>
          <h4 className="text-lg font-semibold mb-4 text-brand-dark">Financial Metrics</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-brand-secondary/10 p-4 rounded-lg border border-brand-secondary/20 shadow-sm">
              <div className="flex items-center mb-2">
                <TrendingUp className="h-5 w-5 text-brand-secondary mr-2" />
                <h4 className="font-semibold text-brand-dark">TAM</h4>
              </div>
              <p className="text-2xl font-bold text-brand-primary">{formatCurrency(tam)}</p>
              <p className="text-sm text-brand-dark mt-1">Total Addressable Market</p>
            </div>
            
            <div className="bg-brand-primary/10 p-4 rounded-lg border border-brand-primary/20 shadow-sm">
              <div className="flex items-center mb-2">
                <Users className="h-5 w-5 text-brand-primary mr-2" />
                <h4 className="font-semibold text-brand-dark">SAM</h4>
              </div>
              <p className="text-2xl font-bold text-brand-primary">{formatCurrency(sam)}</p>
              <p className="text-sm text-brand-dark mt-1">Serviceable Available Market</p>
            </div>
            
            <div className="bg-brand-secondary/10 p-4 rounded-lg border border-brand-secondary/20 shadow-sm">
              <div className="flex items-center mb-2">
                <Target className="h-5 w-5 text-brand-secondary mr-2" />
                <h4 className="font-semibold text-brand-dark">SOM</h4>
              </div>
              <p className="text-2xl font-bold text-brand-primary">{formatCurrency(som)}</p>
              <p className="text-sm text-brand-dark mt-1">Serviceable Obtainable Market</p>
            </div>
          </div>
          <MarketSizeChart tam={tam} sam={sam} som={som} currency={currency} type="money" />
        </div>
      )}

      {showCustomerMetrics && (
        <div>
          <h4 className="text-lg font-semibold mb-4 text-brand-dark">Customer Metrics</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-brand-secondary/10 p-4 rounded-lg border border-brand-secondary/20 shadow-sm">
              <div className="flex items-center mb-2">
                <TrendingUp className="h-5 w-5 text-brand-secondary mr-2" />
                <h4 className="font-semibold text-brand-dark">TAM</h4>
              </div>
              <p className="text-2xl font-bold text-brand-primary">{formatCustomers(tamCustomers)}</p>
              <p className="text-sm text-brand-dark mt-1">Total Addressable Customers</p>
            </div>
            
            <div className="bg-brand-primary/10 p-4 rounded-lg border border-brand-primary/20 shadow-sm">
              <div className="flex items-center mb-2">
                <Users className="h-5 w-5 text-brand-primary mr-2" />
                <h4 className="font-semibold text-brand-dark">SAM</h4>
              </div>
              <p className="text-2xl font-bold text-brand-primary">{formatCustomers(samCustomers)}</p>
              <p className="text-sm text-brand-dark mt-1">Serviceable Available Customers</p>
            </div>
            
            <div className="bg-brand-secondary/10 p-4 rounded-lg border border-brand-secondary/20 shadow-sm">
              <div className="flex items-center mb-2">
                <Target className="h-5 w-5 text-brand-secondary mr-2" />
                <h4 className="font-semibold text-brand-dark">SOM</h4>
              </div>
              <p className="text-2xl font-bold text-brand-primary">{formatCustomers(somCustomers)}</p>
              <p className="text-sm text-brand-dark mt-1">Serviceable Obtainable Customers</p>
            </div>
          </div>
          <MarketSizeChart tam={tamCustomers} sam={samCustomers} som={somCustomers} currency={currency} type="customers" />
        </div>
      )}
    </div>
  );
};
